import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * useColumnResize — the drag-to-resize half of `AppDataTable`.
 *
 * Lives here rather than in the table for the reason `useAnchoredLayer` does:
 * the mechanics (measure, capture the pointer, remember the width) are a
 * self-contained problem, and `AppDataTable` is already the file everyone reads
 * to learn how a column is declared.
 *
 * ── Why the table measures itself before it can be resized ───────────────────
 *
 * A resizable table has to be `table-layout: fixed` — under `auto` a width is a
 * *hint* the browser may overrule from the content, so a column springs back
 * mid-drag. But flipping to `fixed` with no widths collapses every column to an
 * equal share, which is a table that rearranges itself the moment it loads.
 *
 * So the first paint after the rows arrive is still `auto`: this hook reads the
 * width the browser chose for every header cell, and only then switches to
 * `fixed` with those exact numbers. It runs in a LAYOUT effect, so the measure
 * and the switch happen in one pass and nothing flashes. The visible result of
 * turning resizing on is therefore *nothing* — the table looks exactly as it
 * did, until someone drags.
 *
 * Those measured widths are also what "natural width" means for the rest of the
 * session: double-clicking a handle restores one, which is why they are kept
 * even when a stored width immediately replaces them.
 *
 * ── Where the leftover space goes ───────────────────────────────────────────
 *
 * ⚠️ Under `fixed`, a table wider than the sum of its columns hands the excess
 * out PROPORTIONALLY — every column grows a little. That is fatal for a drag: a
 * column narrowed by 100px would come to rest somewhere other than where it was
 * dropped, because part of the space it gave up is handed straight back to it.
 *
 * So the slack goes to ONE column instead — the last one that is neither pinned
 * right (a frozen actions column has a width it was given deliberately) nor
 * sized by hand. The rendered widths then sum to exactly the container and the
 * browser has nothing left to distribute. Wider than the container, the table
 * scrolls, which is the whole point of dragging a column out.
 *
 * ⚠️ "Nor sized by hand" is the half that is easy to drop and reads as a broken
 * drag: a column that absorbs the slack absorbs *its own* slack, so shrinking it
 * by 60px hands 60px straight back and the drag looks dead. Dragging a column
 * therefore takes it out of the pool for good, and the role moves left to a
 * column the user has not spoken about. Once every column has been sized the
 * last one takes the slack again — by then there is nowhere else for it to go.
 *
 * ── Persistence ─────────────────────────────────────────────────────────────
 *
 * `storageKey` remembers widths per browser under `vc-table-widths:<key>`.
 * Stored values are read once, on mount, and validated: a key from a column
 * that no longer exists is ignored, and a value below the column's minimum is
 * raised rather than obeyed — nothing in localStorage may produce a column too
 * narrow to read. Without a `storageKey` the widths last as long as the page is
 * mounted, which is the right default for a table nobody lives in.
 */

/** Narrower than this and a header label has nowhere to go. Overridable per
 *  column with `minWidth`, for a column that really is just an icon. */
const DEFAULT_MIN_WIDTH = 72;
/** One arrow-key press. Shift multiplies it — 16px at a time across a 400px
 *  column is twenty-five presses. */
const KEY_STEP = 16;
const KEY_STEP_LARGE = 64;
/** A drag fires a move per frame; the widths are only worth writing once the
 *  hand stops. */
const PERSIST_DELAY = 300;
const STORAGE_PREFIX = "vc-table-widths:";

const minWidthOf = (col) => Number(col.minWidth) || DEFAULT_MIN_WIDTH;

const readStored = (storageKey) => {
  if (!storageKey || typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_PREFIX + storageKey) || "null");
    if (!parsed || typeof parsed !== "object") return {};
    const clean = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (Number.isFinite(value) && value > 0) clean[key] = Math.round(value);
    }
    return clean;
  } catch (err) {
    // Unparseable, or storage denied (private mode, blocked cookies). A table
    // that cannot remember a width is not a table that should fail to render.
    return {};
  }
};

const writeStored = (storageKey, widths) => {
  if (!storageKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(widths));
  } catch (err) {
    // Quota, or denied storage — the widths still hold for this page view.
  }
};

/**
 * @param {object}   options
 * @param {Array}    options.columns    the same column list the table renders
 * @param {boolean}  options.enabled    `resizable` on the table
 * @param {boolean}  options.loading    widths are measured only once real rows are in
 * @param {string=}  options.storageKey persist under this name
 * @param {object}   options.scrollRef  ref to the table's scroll container
 */
export default function useColumnResize({ columns, enabled, loading, storageKey, scrollRef }) {
  const [widths, setWidths] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const headRefs = useRef(new Map());
  const naturalRef = useRef({});
  const dragRef = useRef(null);
  /** Columns the user has set a width on — they stop absorbing the slack. A ref
   *  rather than state: it only ever changes alongside a width, and that
   *  re-renders anyway. */
  const sizedRef = useRef(new Set());
  const storedRef = useRef(null);
  if (storedRef.current === null) storedRef.current = readStored(storageKey);

  // Read inside effects and handlers that must not re-run when a width changes.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const setHeadRef = useCallback(
    (key) => (el) => {
      if (el) headRefs.current.set(key, el);
      else headRefs.current.delete(key);
    },
    []
  );

  // ── Seed: measure whatever has no width yet, then let a stored value win ───
  // Keyed on the column KEYS, not on the array: a page rebuilds its column list
  // whenever a callback identity changes, and re-measuring on that would undo a
  // drag. A genuinely new column changes this string and gets measured.
  const colKeys = columns.map((c) => c.key).join(" ");
  useLayoutEffect(() => {
    if (!enabled || loading) return;
    const cols = columnsRef.current;
    if (cols.length === 0) return;
    const prev = widthsRef.current;
    if (cols.every((c) => prev[c.key] != null)) return;

    const next = { ...prev };
    for (const col of cols) {
      if (next[col.key] != null) continue;
      const el = headRefs.current.get(col.key);
      const measured = el ? Math.floor(el.getBoundingClientRect().width) : 0;
      const natural = Math.max(minWidthOf(col), measured || Number(col.width) || 140);
      naturalRef.current[col.key] = natural;
      const stored = storedRef.current[col.key];
      // A stored width is a width this user chose, in an earlier session — it
      // counts as sized, or the column it was chosen for would go on absorbing
      // whatever the others gave up.
      if (stored) sizedRef.current.add(col.key);
      next[col.key] = stored ? Math.max(minWidthOf(col), stored) : natural;
    }
    setWidths(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, loading, colKeys]);

  // ── The container, for the slack calculation ──────────────────────────────
  useEffect(() => {
    const el = scrollRef?.current;
    if (!enabled || !el || typeof ResizeObserver === "undefined") return undefined;
    setContainerWidth(Math.floor(el.clientWidth));
    const observer = new ResizeObserver(([entry]) =>
      setContainerWidth(Math.floor(entry.contentRect.width))
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, scrollRef]);

  // ── Persist, after the hand stops ─────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !storageKey || Object.keys(widths).length === 0) return undefined;
    const id = setTimeout(() => writeStored(storageKey, widths), PERSIST_DELAY);
    return () => clearTimeout(id);
  }, [enabled, storageKey, widths]);

  // While dragging, the cursor is the drag's — not whatever it happens to be
  // over. Without this it flickers back to the row's pointer the moment the
  // pointer leaves the 10px handle, which reads as the drag having ended.
  useEffect(() => {
    if (!activeKey) return undefined;
    const { style } = document.body;
    const prevCursor = style.cursor;
    const prevSelect = style.userSelect;
    style.cursor = "col-resize";
    style.userSelect = "none";
    return () => {
      style.cursor = prevCursor;
      style.userSelect = prevSelect;
    };
  }, [activeKey]);

  const resize = useCallback((col, nextWidth) => {
    const min = minWidthOf(col);
    sizedRef.current.add(col.key);
    setWidths((prev) => {
      const value = Math.max(min, Math.round(nextWidth));
      return prev[col.key] === value ? prev : { ...prev, [col.key]: value };
    });
  }, []);

  /** Back to the width the browser measured — and back into the slack pool,
   *  since the user has withdrawn the width they chose. */
  const resetToNatural = useCallback((col) => {
    const natural = naturalRef.current[col.key];
    if (!natural) return;
    sizedRef.current.delete(col.key);
    setWidths((prev) => (prev[col.key] === natural ? prev : { ...prev, [col.key]: natural }));
  }, []);

  /** Props for one column's drag handle, or `null` where the column opts out. */
  const resizerProps = useCallback(
    (col) => {
      if (col.resizable === false) return null;
      const label = typeof col.label === "string" && col.label ? col.label : col.key;
      return {
        role: "separator",
        "aria-orientation": "vertical",
        "aria-label": `Resize the ${label} column`,
        tabIndex: 0,
        onPointerDown: (e) => {
          if (e.button !== 0) return;
          const startWidth = widthsRef.current[col.key];
          if (startWidth == null) return;
          // The handle sits inside a header cell that sorts on click, and in
          // children mode could sit inside anything at all.
          e.preventDefault();
          e.stopPropagation();
          dragRef.current = { key: col.key, startX: e.clientX, startWidth };
          // Pointer capture, so the drag survives the pointer leaving the 10px
          // handle — which it does immediately, that being the point of a drag.
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch (err) {
            // Capture is a nicety; moves still arrive while over the handle.
          }
          setActiveKey(col.key);
        },
        onPointerMove: (e) => {
          const drag = dragRef.current;
          if (!drag || drag.key !== col.key) return;
          resize(col, drag.startWidth + (e.clientX - drag.startX));
        },
        onPointerUp: (e) => {
          if (!dragRef.current) return;
          dragRef.current = null;
          setActiveKey(null);
          try {
            e.currentTarget.releasePointerCapture(e.pointerId);
          } catch (err) {
            // Already released with the pointer.
          }
        },
        onPointerCancel: () => {
          dragRef.current = null;
          setActiveKey(null);
        },
        // A resize is a drag and a drag has no click — but the header cell
        // underneath sorts on one, so a released drag must not also reorder the
        // table.
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        // Back to the width the browser chose from the content.
        onDoubleClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
          resetToNatural(col);
        },
        onKeyDown: (e) => {
          const current = widthsRef.current[col.key];
          if (current == null) return;
          const step = e.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
          if (e.key === "ArrowLeft") {
            e.preventDefault();
            resize(col, current - step);
          } else if (e.key === "ArrowRight") {
            e.preventDefault();
            resize(col, current + step);
          } else if (e.key === "Enter" || e.key === " ") {
            // The keyboard's double-click: there is no drag to end, so
            // activation is free to mean "put it back".
            e.preventDefault();
            resetToNatural(col);
          }
        },
      };
    },
    [resize, resetToNatural]
  );

  const seeded = enabled && columns.length > 0 && columns.every((c) => widths[c.key] != null);

  let total = 0;
  let slackKey = null;
  if (seeded) {
    for (const col of columns) total += widths[col.key];
    const candidates = columns.filter((c) => c.pinned !== "right" && c.resizable !== false);
    const untouched = candidates.filter((c) => !sizedRef.current.has(c.key));
    const pool = untouched.length ? untouched : candidates;
    slackKey = pool.length ? pool[pool.length - 1].key : null;
  }
  const slack =
    seeded && slackKey && containerWidth > total ? Math.floor(containerWidth - total) : 0;

  return {
    /** Widths are known — the table may go `fixed` and render its `<colgroup>`. */
    seeded,
    /** The column being dragged right now, for the handle's active styling. */
    activeKey,
    setHeadRef,
    resizerProps,
    tableStyle: seeded
      ? { tableLayout: "fixed", width: slack > 0 ? "100%" : `${total}px` }
      : undefined,
    colWidth: (col) => widths[col.key] + (col.key === slackKey ? slack : 0),
  };
}
